import React from 'react';
import { TrashedItem, Task, Project } from '../../../types';

interface TrashModalProps {
  isOpen: boolean;
  onClose: () => void;
  trashItems: TrashedItem[];
  onRestore: (item: TrashedItem) => void;
  onPermanentDelete: (trashId: string) => void;
  onEmptyTrash: () => void;
}

export const TrashModal: React.FC<TrashModalProps> = ({
  isOpen,
  onClose,
  trashItems,
  onRestore,
  onPermanentDelete,
  onEmptyTrash,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 sm:backdrop-blur-sm flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-lg shadow-xl w-full h-full sm:w-auto sm:max-w-xl overflow-hidden text-slate-800 sm:max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-lg text-slate-800">Trash Can</h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
              {trashItems.length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {trashItems.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('Permanently delete all items in trash?')) onEmptyTrash();
                }}
                className="text-xs text-red-600 hover:text-red-700 font-semibold"
              >
                Empty Trash
              </button>
            )}
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 font-bold text-lg">
              ✕
            </button>
          </div>
        </div>

        {/* Item List */}
        <div className="p-3 sm:p-4 overflow-y-auto space-y-2 flex-1">
          {trashItems.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <p className="text-sm">Trash is empty</p>
            </div>
          ) : (
            trashItems.map((item) => {
              const name = item.type === 'task' ? (item.originalData as Task).title : (item.originalData as Project).name;

              return (
                <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                      item.type === 'task' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {item.type}
                    </span>
                    <span className="font-medium text-slate-700 truncate">{name}</span>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => onRestore(item)}
                      className="px-3 py-1 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-xs font-semibold rounded"
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      onClick={() => onPermanentDelete(item.id)}
                      className="px-2 py-1 text-slate-400 hover:text-red-600 text-xs font-semibold rounded"
                      title="Delete Permanently"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex flex-wrap justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};